import { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { X, Search, ArrowUpDown, ChevronDown, Edit2, Save, Plus, Trash2, ChevronRight } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchAdditionalContextPresets, 
  addAdditionalContextPreset, 
  updateAdditionalContextPreset, 
  deleteAdditionalContextPreset 
} from '../../store/reducers';
import { filterAdditionalContextTemplates, sortAdditionalContextTemplates } from '../../utils/additionalContextUtils';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import toast from 'react-hot-toast';

export const AdditionalContextModal = memo(({ isOpen, onClose, onSelect, isSpanish, openInCreateMode = false }) => {
  const dispatch = useDispatch();
  
  // Get presets from Redux
  const templates = useSelector((state) => {
    const presets = state.additionalContextPresets;
    return Array.isArray(presets) ? presets : [];
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedTemplateForDetails, setSelectedTemplateForDetails] = useState(null);
  const [shouldShowMainModal, setShouldShowMainModal] = useState(isOpen);
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Sync shouldShowMainModal with isOpen prop and reset state when modal opens
  // Fetch presets from API whenever the modal is opened or closed
  useEffect(() => {
    if (isOpen) {
      setShouldShowMainModal(true);
      setSelectedTemplateForDetails(null);
      setOpenInEditMode(false);
      dispatch(fetchAdditionalContextPresets());
      
      // If openInCreateMode is true, open directly in create mode
      if (openInCreateMode) {
        const newTemplate = {
          id: `act-${Date.now()}`,
          name: '',
          content: '',
        };
        setSelectedTemplateForDetails(newTemplate);
        setShouldShowMainModal(false);
        setOpenInEditMode(true);
      }
    } else {
      // Also fetch when modal closes to ensure data is fresh
      dispatch(fetchAdditionalContextPresets());
    }
  }, [isOpen, openInCreateMode, dispatch]);

  const sortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
    { value: 'content-asc', label: isSpanish ? 'Contenido (A-Z)' : 'Content (A-Z)' },
    { value: 'content-desc', label: isSpanish ? 'Contenido (Z-A)' : 'Content (Z-A)' },
  ];

  const filteredTemplates = useMemo(() => {
    const filtered = filterAdditionalContextTemplates(templates, searchQuery);
    return sortAdditionalContextTemplates(filtered, sortBy);
  }, [templates, searchQuery, sortBy]);

  const handleSelect = useCallback((template) => {
    if (onSelect && template?.content) {
      onSelect(template.content);
      setSearchQuery('');
      setIsSortOpen(false);
      setShouldShowMainModal(false);
      onClose();
    }
  }, [onSelect, onClose]);

  const openTemplateDetails = useCallback((template, e) => {
    e.stopPropagation();
    setSelectedTemplateForDetails(template);
    setShouldShowMainModal(false);
    // Don't call onClose() - we want to keep the modal open, just switch to details view
  }, []);

  const closeTemplateDetails = useCallback(() => {
    setSelectedTemplateForDetails(null);
    setShouldShowMainModal(true);
  }, []);

  const createNewTemplate = useCallback(() => {
    const newTemplate = {
      id: `act-${Date.now()}`,
      content: '',
    };
    setSelectedTemplateForDetails(newTemplate);
    setShouldShowMainModal(false);
    setOpenInEditMode(true);
    // Don't call onClose() - we want to keep the modal open, just switch to details view
  }, []);

  const handleTemplateSaved = useCallback((savedTemplate) => {
    setSelectedTemplateForDetails(savedTemplate);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* Main Template Modal */}
      {shouldShowMainModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn !m-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 animate-modalEnter">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{isSpanish ? 'Plantillas de Contexto' : 'Context Templates'}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={createNewTemplate}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 text-default rounded-full hover:opacity-90 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  {isSpanish ? 'Nueva' : 'New'}
                </button>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setIsSortOpen(false);
                    setShouldShowMainModal(false);
                    onClose();
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <div className="relative flex items-center gap-2 border-container rounded-full px-3 py-1 flex-1">
                <Search className="h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  className="pr-4 py-2 !border-none no-border search-input text-sm flex-1"
                  placeholder={isSpanish ? 'Buscar...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="relative">
                <button
                  className="flex items-center gap-2 px-2 py-2 rounded-full text-sm"
                  onClick={() => setIsSortOpen(!isSortOpen)}
                >
                  <ArrowUpDown className="w-4 h-4" />
                  <span>{sortOptions.find(o => o.value === sortBy)?.label || ''}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSortOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                          sortBy === option.value ? 'text-primary' : 'text-gray-700'
                        }`}
                        onClick={() => {
                          setSortBy(option.value);
                          setIsSortOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="h-[60vh] overflow-y-auto">
              {filteredTemplates.length > 0 ? (
                <div className="space-y-2">
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="border-bottom-gray hover:bg-gray-100 p-3 px-0 cursor-pointer"
                      onClick={() => handleSelect(template)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 mb-1">{template.name || (isSpanish ? 'Sin nombre' : 'Untitled')}</p>
                          <p className="text-sm text-gray-700 line-clamp-2">{template.content}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => openTemplateDetails(template, e)}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 bg-gray-200a rounded-fulla px-0 py-1"
                          >
                            <span>{isSpanish ? 'Ver detalles' : 'View details'}</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {isSpanish ? 'No se encontraron plantillas' : 'No templates found'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Details Modal */}
      {selectedTemplateForDetails && (
        <AdditionalContextDetailsModal
          template={selectedTemplateForDetails}
          templates={templates}
          isOpen={!!selectedTemplateForDetails}
          onClose={(shouldCloseAll = false) => {
            if (shouldCloseAll) {
              // Close everything if explicitly requested (e.g., new template cancelled)
              setSelectedTemplateForDetails(null);
              setShouldShowMainModal(false);
              onClose();
            } else {
              // Otherwise, just go back to main modal
              closeTemplateDetails();
            }
          }}
          isSpanish={isSpanish}
          openInEditMode={openInEditMode}
          onEditModeChange={(isEdit) => setOpenInEditMode(false)}
          onTemplateSaved={handleTemplateSaved}
          onSelect={onSelect}
        />
      )}
    </>
  );
});

AdditionalContextModal.displayName = 'AdditionalContextModal';

// Template Details Modal Component
const AdditionalContextDetailsModal = ({
  template,
  templates = [],
  isOpen,
  onClose,
  isSpanish,
  openInEditMode = false,
  onEditModeChange,
  onTemplateSaved,
  onSelect,
}) => {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState(() => {
    if (template && typeof template === 'object') {
      return {
        id: template.id || '',
        name: template.name !== undefined ? (template.name || '') : '',
        content: template.content || '',
      };
    }
    return {
      id: '',
      name: '',
      content: '',
    };
  });

  // Check if this is a new template (not yet saved)
  const isNewTemplate = template && 
    typeof template === 'object' && 
    template.id && 
    typeof template.id === 'string' && 
    template.id.startsWith('act-') &&
    (!template.content && !template.name); // New templates have empty content and name initially

  // Handle opening in edit mode
  useEffect(() => {
    if (openInEditMode) {
      setIsEditMode(true);
      if (onEditModeChange) {
        onEditModeChange(true);
      }
    }
  }, [openInEditMode, onEditModeChange]);

  useEffect(() => {
    if (template && typeof template === 'object' && !isEditMode) {
      setEditedTemplate({
        id: template.id || '',
        name: template.name !== undefined ? (template.name || '') : '',
        content: template.content || '',
      });
    }
    if (openInEditMode && onEditModeChange) {
      setTimeout(() => {
        if (onEditModeChange) {
          onEditModeChange(false);
        }
      }, 0);
    }
  }, [template, openInEditMode, onEditModeChange, isEditMode]);

  const handleSave = useCallback(async () => {
    if (!editedTemplate?.content?.trim()) {
      toast.error(isSpanish ? 'Por favor ingrese contenido' : 'Please enter content');
      return;
    }

    const presetToSave = {
      id: editedTemplate.id,
      name: editedTemplate.name?.trim() || null, // Allow null name
      content: editedTemplate.content.trim(),
    };

    // Check if preset exists before updating to determine toast message
    const presetExists = Array.isArray(templates) && templates.some(t => t.id === presetToSave.id);
    const isUpdate = presetExists && typeof presetToSave.id !== 'string';

    try {
      let result;
      if (isUpdate) {
        // Update existing preset
        result = await dispatch(updateAdditionalContextPreset(presetToSave));
      } else {
        // Create new preset (don't include id for new presets)
        const { id, ...newPreset } = presetToSave;
        result = await dispatch(addAdditionalContextPreset(newPreset));
      }

      if (result && result.success) {
        // Show toast
        if (isUpdate) {
          toast.success(isSpanish ? 'Plantilla actualizada' : 'Template updated');
        } else {
          toast.success(isSpanish ? 'Plantilla creada' : 'Template created');
        }

        // Update selected template with saved preset
        if (result.preset && onTemplateSaved) {
          onTemplateSaved(result.preset);
        }

        // Refresh presets from API
        dispatch(fetchAdditionalContextPresets());

        setIsEditMode(false);
      } else {
        const errorMessage = result?.error || (isSpanish ? 'Error al guardar la plantilla' : 'Error saving template');
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving preset:', error);
      toast.error(isSpanish ? 'Error al guardar la plantilla' : 'Error saving template');
    }
  }, [editedTemplate, templates, isSpanish, dispatch, onTemplateSaved]);

  const handleDeleteClick = useCallback(() => {
    if (isNewTemplate) {
      return;
    }

    if (!template || !template.id) {
      toast.error(isSpanish ? 'Error: La plantilla no tiene un ID válido' : 'Error: Template does not have a valid ID');
      return;
    }

    setShowDeleteConfirmation(true);
  }, [template, isNewTemplate, isSpanish]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!template || !template.id) {
      return;
    }

    try {
      const result = await dispatch(deleteAdditionalContextPreset(template.id));
      
      if (result && result.success) {
        toast.success(isSpanish ? 'Plantilla eliminada' : 'Template deleted');
        
        // Refresh presets from API
        dispatch(fetchAdditionalContextPresets());
        
        // After deleting, go back to main modal (don't close everything)
        setShowDeleteConfirmation(false);
        onClose(false);
      } else {
        const errorMessage = result?.error || (isSpanish ? 'Error al eliminar la plantilla' : 'Error deleting template');
        toast.error(errorMessage);
        setShowDeleteConfirmation(false);
      }
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast.error(isSpanish ? 'Error al eliminar la plantilla' : 'Error deleting template');
      setShowDeleteConfirmation(false);
    }
  }, [template, isSpanish, dispatch, onClose]);

  const handleCancel = useCallback(() => {
    if (isNewTemplate) {
      // For new templates, close everything
      onClose(true);
      return;
    }
    
    if (template && typeof template === 'object') {
      setEditedTemplate({
        id: template.id || '',
        name: template.name !== undefined ? (template.name || '') : '',
        content: template.content || '',
      });
    }
    setIsEditMode(false);
  }, [template, isNewTemplate, onClose]);

  const handleUseTemplate = useCallback(() => {
    if (onSelect && editedTemplate?.content) {
      onSelect(editedTemplate.content);
      // After using template, close everything
      onClose(true);
    }
  }, [onSelect, editedTemplate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-999 !m-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] h-[80vh] flex flex-col animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isSpanish ? 'Detalles de Plantilla' : 'Template Details'}</h2>
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <>
                <button
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-gray-100 text-default rounded-full"
                >
                  <Edit2 className="w-4 h-4" />
                  {isSpanish ? 'Editar' : 'Edit'}
                </button>
                {!isNewTemplate && (
                  <button
                    onClick={handleDeleteClick}
                    className="flex items-center gap-1 px-4 py-2 text-sm bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 stroke-red-600" />
                    {isSpanish ? 'Eliminar' : 'Delete'}
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1 px-4 py-2 text-sm bg-default text-white rounded-full"
                >
                  <Save className="w-4 h-4 stroke-white" />
                  {isSpanish ? 'Guardar' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm bg-gray-100 text-default rounded-full"
                >
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </button>
              </>
            )}
            <button
              onClick={() => {
                // If it's a new unsaved template, close everything; otherwise go back to main modal
                if (isNewTemplate) {
                  onClose(true);
                } else {
                  onClose(false);
                }
              }}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Name and Content Section */}
        <div className="mb-4 pb-4">
          {isEditMode ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{isSpanish ? 'Nombre' : 'Name'}</label>
                <input
                required
                  type="text"
                  value={editedTemplate?.name || ''}
                  onChange={(e) => setEditedTemplate(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border-container rounded-lg"
                  placeholder={isSpanish ? 'Ingrese el nombre...' : 'Enter name...'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{isSpanish ? 'Contenido' : 'Content'}</label>
                <textarea
                required
                  value={editedTemplate?.content || ''}
                  onChange={(e) => setEditedTemplate(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border-container rounded-lg min-h-[400px] text-sm"
                  placeholder={isSpanish ? 'Ingrese el contenido...' : 'Enter content...'}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{isSpanish ? 'Nombre' : 'Name'}</label>
                <div className="w-full py-2">
                  <p className="text-lg font-semibold">{editedTemplate?.name || (isSpanish ? 'Sin nombre' : 'Untitled')}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{isSpanish ? 'Contenido' : 'Content'}</label>
                <div className="w-full py-2 min-h-[400px] whitespace-pre-wrap text-sm">
                  {editedTemplate?.content || (isSpanish ? 'Sin contenido' : 'No content')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        title={isSpanish ? 'Eliminar plantilla' : 'Delete Template'}
        message={isSpanish 
          ? '¿Está seguro de que desea eliminar esta plantilla? Esta acción no se puede deshacer.'
          : 'Are you sure you want to delete this template? This action cannot be undone.'}
        itemName={template?.name || (template?.content ? (template.content.length > 50 ? `${template.content.substring(0, 50)}...` : template.content) : '')}
        isSpanish={isSpanish}
        confirmButtonText={isSpanish ? 'Eliminar' : 'Delete'}
        confirmButtonColor="red"
        iconColor="red"
      />
    </div>
  );
};

